/**
 * commandCenterBus — tiny module-level pub/sub used to bridge the
 * single admin WebSocket connection (owned by useAdminFirehose, mounted
 * at the top of AdminPanelPage) to the CommandCenterTab without
 * passing props all the way down or opening a second WS.
 *
 * Why not Context? — The firehose is already a side-effecting hook;
 * wrapping the whole admin tree in a provider just to ferry alert
 * events would over-couple a feature that should be optional. A flat
 * bus is enough, and tabs that don't care never pay the cost.
 *
 * Events emitted (raw WS message payloads):
 *   • "alert.created"
 *   • "alert.acknowledged"
 *   • "alert.status_changed"
 *   • "alert.dispatched"
 *   • "alert.reassigned"
 *   • "alert.review"
 *   • "alert.operational"
 *   • "ws.live"            — { live: boolean }
 *
 * Subscribers are responsible for their own debouncing / batching.
 */
const listeners = new Map(); // event → Set<fn>
// Some events represent a CURRENT STATE (e.g. ``ws.live``) rather than
// a one-shot signal. Subscribers that mount *after* the last emit need
// to know the current value immediately or their UI flickers
// "Hors-ligne" until the next state change — for ``ws.live`` that can
// be many minutes on a healthy connection. We snapshot those values
// here and expose them via ``ccBus.last(event)``.
const last = new Map(); // event → most recent payload
const _STATEFUL_EVENTS = new Set(["ws.live"]);

export const ccBus = {
  on(event, fn) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(fn);
    // Replay the last snapshot for stateful events so a fresh
    // subscriber doesn't wait for the next emit.
    if (_STATEFUL_EVENTS.has(event) && last.has(event)) {
      try { fn(last.get(event)); } catch (e) { console.warn(`[ccBus] replay for ${event} threw:`, e); }
    }
    return () => ccBus.off(event, fn);
  },
  off(event, fn) {
    listeners.get(event)?.delete(fn);
  },
  emit(event, payload) {
    if (_STATEFUL_EVENTS.has(event)) last.set(event, payload);
    const subs = listeners.get(event);
    if (!subs) return;
    // Snapshot before iteration so a subscriber unsubscribing during
    // dispatch never trips a "Set was modified" surprise.
    Array.from(subs).forEach((fn) => {
      try { fn(payload); } catch (e) { console.warn(`[ccBus] subscriber for ${event} threw:`, e); }
    });
  },
  last(event) {
    return last.get(event);
  },
};

export default ccBus;
